import { FormHeaderAndContent, FormPageContainer } from '@strata/react-form-header-and-content';
import { FormAccordion } from '@strata/react-form-section';
import { FullscreenLoading } from '@strata/react-fullscreen-loading';
import { Formik, getIn } from 'formik';
import { useState } from 'react';

import { ConfirmPatientDialog } from 'components/shared/ConfirmPatientDialog';
import { AuthDetailsSection } from 'components/tools/priorAuthorization/AuthDetailsSection';
import { PatientDetailsHeader } from 'components/tools/priorAuthorization/PatientDetailsHeader';
import { PatientSection } from 'components/tools/priorAuthorization/PatientSection';
import { PriorAuthorizationHeader } from 'components/tools/priorAuthorization/PriorAuthorizationHeader';
import { ProductSection } from 'components/tools/priorAuthorization/ProductSection';
import {
    Sections,
    SectionToStepMap,
    StepToSectionMap,
    validationSchema,
} from 'services/priorAuthorizationHelpers';

const initialValues = {
    [Sections.PATIENT]: {
        pharmacy: undefined,
        healthPlan: undefined,
        patientFirstName: undefined,
        patientLastName: undefined,
        cardholderId: undefined,
        eligibility: undefined,
    },
    [Sections.AUTH_DETAILS]: {
        flags: [],
    },
};

export const PriorAuthorization = () => {
    const [loadingText, setLoadingText] = useState();
    const [showPatientDialog, setShowPatientDialog] = useState(false);
    const [openSection, setOpenSection] = useState(SectionToStepMap[Sections.PATIENT]);

    return (
        <FormPageContainer>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount
                enableReinitialize
            >
                {(formik) => {
                    const getSectionStatus = (sectionId) => {
                        const sectionTouched = !!getIn(formik.touched, sectionId);
                        if (sectionTouched && !getIn(formik.errors, sectionId)) {
                            return FormAccordion.statuses.COMPLETED;
                        }
                        if (sectionTouched) {
                            return FormAccordion.statuses.ACTIVE;
                        }
                        return '';
                    };

                    const isSectionComplete = (status) =>
                        status === FormAccordion.statuses.COMPLETED;

                    const handleSectionClick = (step) => () => {
                        const previousSections = [...Array(step - 1)].map((_v, i) =>
                            getSectionStatus(StepToSectionMap[i + 1]),
                        );
                        if (openSection !== step && previousSections.every(isSectionComplete)) {
                            setOpenSection(step);
                        }
                    };

                    const getSectionProps = (sectionId) => {
                        const step = SectionToStepMap[sectionId];

                        return {
                            step,
                            progressIndicatorStatus: getSectionStatus(sectionId),
                            onSectionClick: handleSectionClick(step),
                            isOpen: openSection === step,
                        };
                    };

                    const onClear = () => {
                        formik.resetForm({
                            values: initialValues,
                            touched: {},
                        });
                        setOpenSection(SectionToStepMap[Sections.PATIENT]);
                    };

                    const sectionDataArray = [
                        {
                            sectionTitle: 'Select Patient',
                            content: (
                                <PatientSection
                                    setOpenSection={setOpenSection}
                                    setShowPatientDialog={setShowPatientDialog}
                                    setLoadingText={setLoadingText}
                                />
                            ),
                            ...getSectionProps(Sections.PATIENT),
                            displayFooter: false,
                            scrollToTopOnOpen: true,
                            disabled: false,
                        },
                        {
                            sectionTitle: 'Select Product',
                            content: (
                                <ProductSection
                                    setLoadingText={setLoadingText}
                                    setOpenSection={setOpenSection}
                                />
                            ),
                            ...getSectionProps(Sections.PRODUCT),
                            displayFooter: false,
                            scrollToTopOnOpen: true,
                            disabled: false,
                        },
                        {
                            sectionTitle: 'Provide Authorization Details',
                            content: (
                                <AuthDetailsSection
                                    setLoadingText={setLoadingText}
                                    onClear={onClear}
                                />
                            ),
                            ...getSectionProps(Sections.AUTH_DETAILS),
                            displayFooter: false,
                            scrollToTopOnOpen: true,
                            disabled: false,
                        },
                    ];

                    const showPatientHeader = getIn(
                        formik.values,
                        `${Sections.PATIENT}.eligibility.eligibilityConfirmed`,
                    );

                    return (
                        <>
                            <FullscreenLoading
                                isShowing={!!loadingText}
                                loadingText={loadingText ?? ''}
                            />
                            <ConfirmPatientDialog
                                isOpen={showPatientDialog}
                                patientDetails={formik.values[Sections.PATIENT]?.eligibility}
                                onConfirm={async () => {
                                    setShowPatientDialog(false);
                                    await formik.setFieldValue(
                                        `${Sections.PATIENT}.eligibility.eligibilityConfirmed`,
                                        true,
                                    );
                                }}
                                onReturnToForm={() => setShowPatientDialog(false)}
                            />
                            <FormHeaderAndContent
                                rowOneContentHeader={<PriorAuthorizationHeader onClear={onClear} />}
                                rowThreeContentHeader={
                                    showPatientHeader && (
                                        <PatientDetailsHeader
                                            patientDetails={getIn(formik.values, Sections.PATIENT)}
                                            productDetails={getIn(formik.values, Sections.PRODUCT)}
                                        />
                                    )
                                }
                                mainContent={<FormAccordion sectionDataArray={sectionDataArray} />}
                            />
                        </>
                    );
                }}
            </Formik>
        </FormPageContainer>
    );
};
